import { formatDate } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { type ClipboardScoreLog } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreHistory";
import {
  type EmergencyCreditStatsCommon,
  useGetClipboardScoreProfile,
} from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { useDiscardLatenessBooktimeThreshold } from "@src/appV2/ClipboardScore/hooks/featureFlags";
import { ClipboardScoreActions } from "@src/appV2/redesign/ClipboardScore/utils/constants";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useMemo } from "react";

import {
  buildScoreDataForEmergencyCreditsClockOutFlow,
  type EmergencyClockOutFlowShiftData,
} from "../YourClipboardScore/utils/buildScoreDataForEmergencyCreditsClockOutFlow";
import { useEmergencyCreditStats } from "./useEmergencyCreditStats";

interface UseEmergencyCreditsClockOutDataParams {
  scoreLogs: ClipboardScoreLog[] | undefined;
  currentScore: number | undefined;
  scoreChange: number | undefined;
  latenessMinutes: number;
  isSecondShiftInDouble: boolean;
  isFirstShiftInDouble: boolean;
  wasBookedWithShortLeadTime: boolean;
  newlyRestricted: boolean;
  isArrivalTimeFeatureEnabled: boolean;
  formattedTargetArrivalTime?: string;
}

export interface EmergencyCreditInfo {
  cancellationCreditStats: EmergencyCreditStatsCommon;
  latenessCreditStats: EmergencyCreditStatsCommon;
  completedShiftsToDisplayForLatenessCredit: number;
  hasAvailableLatenessCredit: boolean;
  areBothCreditsMaxed: boolean;
  /** Whether the worker can apply a lateness credit on this shift (has credit + has saved points) */
  canUseCredit: boolean;
  /** Whether a cancellation credit was earned on this shift */
  gainedCancellationCredit: boolean;
  /** Whether a lateness credit was earned on this shift */
  gainedLatenessCredit: boolean;
  /** Whether the backend auto-consumed a lateness credit on this shift's clock-out */
  usedLatenessCredit: boolean;
}

interface UseEmergencyCreditsClockOutDataResult {
  emergencyCreditInfo: EmergencyCreditInfo | undefined;
  clipboardScoreData: EmergencyClockOutFlowShiftData | undefined;
  restrictionData: {
    liftTime: string | undefined;
    reEnrollmentScore: number | undefined;
    isFinalRestriction: boolean;
  };
  isProfileLoading: boolean;
}

export function useEmergencyCreditsClockOutData(
  params: UseEmergencyCreditsClockOutDataParams
): UseEmergencyCreditsClockOutDataResult {
  const {
    scoreLogs,
    currentScore,
    scoreChange,
    latenessMinutes,
    isSecondShiftInDouble,
    isFirstShiftInDouble,
    wasBookedWithShortLeadTime,
    newlyRestricted,
    isArrivalTimeFeatureEnabled,
    formattedTargetArrivalTime,
  } = params;

  const { userId: workerId, tmz } = useDefinedWorker();
  const { discardLatenessBooktimeThresholdInHours } = useDiscardLatenessBooktimeThreshold();

  const { data: clipboardScoreProfile, isFetching: isProfileLoading } =
    useGetClipboardScoreProfile(workerId);

  const emergencyCreditStats = useEmergencyCreditStats({ clipboardScoreProfile });
  const hasAvailableLatenessCredit = emergencyCreditStats?.hasAvailableLatenessCredit ?? false;

  // Detect if the backend already auto-consumed a lateness credit during clock-out.
  // Filter by START_SHIFT_LATE action because `usedEmergencyCredit` is shared across
  // credit types (lateness and cancellation), so the action filter disambiguates.
  const usedLatenessCredit =
    scoreLogs?.some(
      (log) =>
        log.action === ClipboardScoreActions.START_SHIFT_LATE && log.usedEmergencyCredit === true
    ) ?? false;

  const effectiveHasCredit = hasAvailableLatenessCredit || usedLatenessCredit;

  const clipboardScoreData = useMemo(() => {
    if (!isDefined(scoreLogs) || !isDefined(currentScore) || !isDefined(scoreChange)) {
      return undefined;
    }

    return buildScoreDataForEmergencyCreditsClockOutFlow({
      scoreLogs,
      currentScore,
      scoreChange,
      latenessMinutes,
      isSecondShiftInDouble,
      isFirstShiftInDouble,
      wasBookedWithShortLeadTime,
      newlyRestricted,
      isArrivalTimeFeatureEnabled,
      formattedTargetArrivalTime,
      shortLeadTimeThresholdInHours: discardLatenessBooktimeThresholdInHours,
      policy: clipboardScoreProfile?.policy,
      hasAvailableLatenessCredit: effectiveHasCredit,
    });
  }, [
    scoreLogs,
    currentScore,
    scoreChange,
    latenessMinutes,
    isSecondShiftInDouble,
    isFirstShiftInDouble,
    wasBookedWithShortLeadTime,
    newlyRestricted,
    isArrivalTimeFeatureEnabled,
    formattedTargetArrivalTime,
    discardLatenessBooktimeThresholdInHours,
    clipboardScoreProfile?.policy,
    effectiveHasCredit,
  ]);

  const canUseCredit = effectiveHasCredit && isDefined(clipboardScoreData?.savedPoints);

  const gainedCancellationCredit =
    scoreLogs?.some((log) => log.gainedCancellationCredit === true) ?? false;
  const gainedLatenessCredit = scoreLogs?.some((log) => log.gainedLatenessCredit === true) ?? false;

  const emergencyCreditInfo: EmergencyCreditInfo | undefined = isDefined(emergencyCreditStats)
    ? {
        ...emergencyCreditStats,
        canUseCredit,
        gainedCancellationCredit,
        gainedLatenessCredit,
        usedLatenessCredit,
      }
    : undefined;

  const reEnrollmentDate = clipboardScoreProfile?.accountStatus?.reEnrollmentDate;
  const liftTime = isDefined(reEnrollmentDate)
    ? formatDate(reEnrollmentDate, { timeZone: tmz })
    : undefined;
  const reEnrollmentScore = clipboardScoreProfile?.restriction?.reEnrollmentScore;
  const isFinalRestriction =
    isDefined(clipboardScoreProfile?.accountStatus?.restrictionDuration) &&
    clipboardScoreProfile.accountStatus.restrictionDuration ===
      clipboardScoreProfile?.policy?.finalRestriction?.duration;

  return {
    emergencyCreditInfo,
    clipboardScoreData,
    restrictionData: {
      liftTime,
      reEnrollmentScore,
      isFinalRestriction,
    },
    isProfileLoading,
  };
}
